import { Suspense, lazy, useState } from 'react';
import { ChevronLeft, ChevronRight, Expand } from 'lucide-react';
import type { PublicPhoto } from '@/api/generated';
import { Photo } from '@/components/ui/photo';
import { photoAlt } from '@/lib/apartments';
import { usePhotoTrack } from '@/components/PhotoTrack';
// The viewer is interaction-only: nobody sees it until they ask for it, and
// it holds no LCP candidate. Loading it with the page would put a fullscreen
// dialog, a focus trap and its icons in the bundle of every guest who only
// ever scrolls past the photos. The carousel itself stays eager — it holds the
// picture the page is judged on.
const PhotoLightbox = lazy(() =>
  import('@/components/PhotoLightbox').then((m) => ({ default: m.PhotoLightbox })),
);
import { THUMB_WIDTH } from '@/lib/img';

// The apartment page's photo gallery.
//
// Before this the only way to see photo 12 was to click the twelfth thumbnail:
// no arrows, no swipe, nothing full screen. On a phone that is the whole
// gallery reduced to a strip of stamps — and the photos are what a guest is
// actually deciding on.
export function PhotoCarousel({
  photos,
  title,
}: {
  photos: PublicPhoto[];
  title: string;
}) {
  const track = usePhotoTrack(photos.length);
  const [lightboxAt, setLightboxAt] = useState<number | null>(null);

  if (photos.length === 0) return null;

  return (
    <div className="space-y-3">
      <div className="relative">
        <div
          ref={track.trackRef}
          onScroll={track.onScroll}
          onKeyDown={track.onKeyDown}
          tabIndex={0}
          role="group"
          aria-label={`${title} — photos`}
          data-testid="carousel-track"
          className="flex snap-x snap-mandatory overflow-x-auto overflow-y-hidden rounded-[var(--radius-card)] border border-[var(--border-hairline)] outline-none [scrollbar-width:none] focus-visible:ring-2 focus-visible:ring-[var(--ollio-gold)] [&::-webkit-scrollbar]:hidden"
        >
          {photos.map((p, i) => (
            <button
              key={p.url}
              type="button"
              onClick={() => setLightboxAt(i)}
              aria-label={`Open ${photoAlt(p, title, i, photos.length)} full screen`}
              className="aspect-[16/9] w-full shrink-0 snap-center bg-[var(--ollio-navy)]"
            >
              <Photo
                src={p.url}
                alt={photoAlt(p, title, i, photos.length)}
                width={p.width ?? undefined}
                height={p.height ?? undefined}
                // Only the first is eager: it is the one on screen, and the
                // page's LCP candidate. Eight more racing it would slow the
                // photo the guest is actually looking at.
                priority={i === 0}
                indicateLoading
                sizes="(min-width: 1152px) 1104px, calc(100vw - 3rem)"
              />
            </button>
          ))}
        </div>

        {photos.length > 1 && (
          <>
            <Arrow side="left" onClick={track.prev} disabled={track.index === 0} />
            <Arrow
              side="right"
              onClick={track.next}
              disabled={track.index === photos.length - 1}
            />
            <span
              aria-live="polite"
              data-testid="carousel-counter"
              className="pointer-events-none absolute bottom-3 right-3 z-10 rounded-full bg-black/55 px-2.5 py-1 text-xs font-medium tabular-nums text-white"
            >
              {track.index + 1} / {photos.length}
            </span>
          </>
        )}

        {/* An explicit affordance for full screen. The photo itself opens it
            too, but nothing about a photo says that it would. */}
        <button
          type="button"
          onClick={() => setLightboxAt(track.index)}
          aria-label="View photos full screen"
          className="absolute bottom-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/55 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-black/75"
        >
          <Expand className="h-3.5 w-3.5" aria-hidden="true" />
          View all
        </button>
      </div>

      {photos.length > 1 && (
        <div data-testid="carousel-thumbs" className="flex gap-2 overflow-x-auto pb-1">
          {photos.map((p, i) => (
            <button
              key={p.url}
              type="button"
              onClick={() => track.goTo(i)}
              aria-label={photoAlt(p, title, i, photos.length)}
              aria-current={i === track.index}
              className={`h-16 w-24 shrink-0 overflow-hidden rounded-[var(--radius-control)] border-2 transition ${
                i === track.index
                  ? 'border-[var(--ollio-gold)]'
                  : 'border-transparent opacity-70 hover:opacity-100'
              }`}
            >
              <Photo src={p.url} alt="" fixedWidth={THUMB_WIDTH} width={96} height={64} />
            </button>
          ))}
        </div>
      )}

      {lightboxAt !== null && (
        <Suspense fallback={null}>
          <PhotoLightbox
            photos={photos}
            title={title}
            startIndex={lightboxAt}
            onClose={() => {
              // Leave the carousel on the photo the guest was looking at full
              // screen — coming back to where you started is not where you were.
              track.goTo(lightboxAt);
              setLightboxAt(null);
            }}
          />
        </Suspense>
      )}
    </div>
  );
}

function Arrow({
  side,
  onClick,
  disabled,
}: {
  side: 'left' | 'right';
  onClick: () => void;
  disabled: boolean;
}) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={side === 'left' ? 'Previous photo' : 'Next photo'}
      className={`absolute top-1/2 z-10 hidden -translate-y-1/2 items-center justify-center rounded-full bg-white/85 p-2.5 text-[var(--ollio-ink)] shadow-[var(--shadow-lg)] transition hover:bg-white disabled:opacity-25 sm:inline-flex ${
        side === 'left' ? 'left-3' : 'right-3'
      }`}
    >
      <Icon className="h-5 w-5" />
    </button>
  );
}
